import { useState } from "react";
import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import { FoodsToggleButton, FoodsToggleButtonType, FoodsIcon } from "@foodpilot/foods";
import { MRT_ColumnDef, createMRTColumnHelper } from "material-react-table";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";
import { PrimaryGrid } from "src/components/Grid/layout/PrimaryGrid";
import { GridPageHeader } from "src/components/Grid/PageGridHeader";
import { GridLink } from "src/components/Grid/GridLink";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { formatDate } from "src/utils/formatting";
import { useGridColumns } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { useGridSortUrlSync } from "src/components/Grid/hooks/gridUrlSync/useGridSortUrlSync";
import { useGridPaginationUrlSync } from "src/components/Grid/hooks/gridUrlSync/useGridPaginationUrlSync";
import { useGridFiltersUrlSync } from "src/components/Grid/hooks/gridUrlSync/useGridFiltersUrlSync";
import { useUrlToApi } from "src/api/_common/params/useUrlToApi";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { useGetAllQuestionnaires, Questionnaire } from "src/api/client-api/questionnaire/useGetAllQuestionnaires";

const gridKey = "questionnaire-grid" as const;
const MAX_VISIBLE_OBJECTS = 4;

type EntityFilter = "all" | "company" | "product" | "ingredient" | "packaging" | "transport";

const ObjectChips = ({ objects }: { objects: string[] }) => {
    const visible = objects.slice(0, MAX_VISIBLE_OBJECTS);
    const remaining = objects.length - MAX_VISIBLE_OBJECTS;

    return (
        <>
            <Stack
                direction="row"
                gap={0.5}
                alignItems="center"
                sx={{ flexWrap: "wrap", maxHeight: "4em", overflow: "hidden" }}
            >
                {visible.map((name, index) => (
                    <Chip key={index} label={name} size="small" variant="outlined" />
                ))}
            </Stack>
            {remaining > 0 && (
                <Typography variant="body-medium" color="primary" sx={{ whiteSpace: "nowrap" }}>
                    + {remaining}
                </Typography>
            )}
        </>
    );
};

const useQuestionnaireColumns = (): MRT_ColumnDef<Questionnaire, any>[] => {
    const { t } = useTranslation();
    const location = useLocation();
    const { currentCampaign } = useFoodpilotContext();
    const columnHelper = createMRTColumnHelper<Questionnaire>();

    const campaignLabel = currentCampaign?.label ?? "";

    return [
        columnHelper.accessor("title", {
            id: "title",
            header: t("settings.questionnaires.qss_family", "QSS Family"),
            enableColumnFilter: true,
            Cell: ({ row }) => {
                const currentUrl = location.pathname + location.search;
                const url = `/settings/questionnaires/${row.original.id}?returnUrl=${encodeURIComponent(currentUrl)}`;
                return (
                    <Stack gap={0.5}>
                        <GridLink to={url} label={row.original.title} />
                        <Typography variant="caption" color="text.secondary">
                            {row.original.targetEntityTypes
                                .map((e) => e.charAt(0).toUpperCase() + e.slice(1))
                                .join(", ")}
                        </Typography>
                    </Stack>
                );
            },
        }),
        columnHelper.accessor((row) => `v${row.currentVersion.versionMajor}.${row.currentVersion.versionMinor}`, {
            id: "currentVersion",
            header: t("settings.questionnaires.version_campaign", "Version · Campaign {{campaign}}", {
                campaign: campaignLabel,
            }),
            enableColumnFilter: false,
            enableSorting: false,
            Cell: ({ row }) => {
                const version =
                    row.original.versions.find((version) =>
                        version.assignments.some((a) => a.campaign.id === currentCampaign?.id),
                    ) ?? row.original.currentVersion;
                const dateStr = version.updatedAt ?? version.createdAt;
                return (
                    <Stack gap={0.25}>
                        <Typography variant="body" sx={{ fontWeight: 600 }}>
                            {`v${version.versionMajor}.${version.versionMinor}`}
                        </Typography>
                        {dateStr && (
                            <Typography variant="caption" color="text.secondary">
                                {formatDate(dateStr)}
                            </Typography>
                        )}
                    </Stack>
                );
            },
        }),
        columnHelper.accessor((row) => `v${row.currentVersion.versionMajor}.${row.currentVersion.versionMinor}`, {
            id: "latestPublished",
            header: t("settings.questionnaires.latest_published", "Latest published"),
            enableColumnFilter: false,
            enableSorting: false,
            Cell: ({ row }) => {
                const v = row.original.currentVersion;
                const dateStr = v.updatedAt ?? v.createdAt;
                return (
                    <Stack gap={0.25}>
                        <Typography variant="body" sx={{ fontWeight: 600 }}>
                            {`v${v.versionMajor}.${v.versionMinor}`}
                        </Typography>
                        {dateStr && (
                            <Typography variant="caption" color="text.secondary">
                                {formatDate(dateStr)}
                            </Typography>
                        )}
                    </Stack>
                );
            },
        }),
        columnHelper.accessor((row) => row.objects.join(", "), {
            id: "objects",
            header: t("settings.questionnaires.linked_entities", "Linked entities"),
            enableColumnFilter: false,
            enableSorting: false,
            Cell: ({ row }) => <ObjectChips objects={row.original.objects} />,
        }),
    ];
};

export const QuestionnairesList = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const [entityFilter, setEntityFilter] = useState<EntityFilter>("all");

    const {
        filters: apiFilters,
        sort: apiSort,
        pagination,
    } = useUrlToApi({
        page: "1",
        itemsPerPage: "20",
    });
    const titleFilter = typeof apiFilters.title === "string" ? { title: apiFilters.title } : {};
    const titleOrder = typeof apiSort["order[title]"] === "string" ? { "order[title]": apiSort["order[title]"] } : {};

    const params = {
        ...titleFilter,
        ...titleOrder,
        ...pagination,
        ...(entityFilter !== "all" ? { entity: entityFilter } : {}),
    };

    const { questionnaires, totalItems, isReady, isRefetching } = useGetAllQuestionnaires({
        params,
    });

    const mrt_columns = useQuestionnaireColumns();
    const gridConfiguration = useGridColumns(mrt_columns, gridKey);

    const gridSorting = useGridSortUrlSync();
    const gridPagination = useGridPaginationUrlSync({
        defaultValue: { page: pagination.page ?? "1", itemsPerPage: pagination.itemsPerPage ?? "20" },
    });
    const gridFilters = useGridFiltersUrlSync({
        defaultValues: { title: { value: "" } },
        isLoading: false,
    });

    const isLoading = !isReady || isRefetching;

    const filterButtons: FoodsToggleButtonType[] = [
        { id: "all", label: t("settings.questionnaires.filter_all", "All"), onClick: () => setEntityFilter("all") },
        {
            id: "product",
            label: t("settings.questionnaires.filter_products", "Products"),
            onClick: () => setEntityFilter("product"),
        },
        {
            id: "ingredient",
            label: t("settings.questionnaires.filter_ingredients", "Ingredients"),
            onClick: () => setEntityFilter("ingredient"),
        },
        {
            id: "company",
            label: t("settings.questionnaires.filter_company", "Company"),
            onClick: () => setEntityFilter("company"),
        },
        {
            id: "packaging",
            label: t("settings.questionnaires.filter_packaging", "Packagings"),
            onClick: () => setEntityFilter("packaging"),
        },
        {
            id: "transport",
            label: t("settings.questionnaires.filter_transport", "Transport"),
            onClick: () => setEntityFilter("transport"),
        },
    ];

    const menuItems: MenuItemType<Questionnaire>[] = [
        {
            icon: <FoodsIcon icon="questionnaires" size={3} />,
            label: t("settings.questionnaires.view", "View"),
            onClick: (entity) => {
                navigate(`/settings/questionnaires/${entity.id}`);
            },
        },
    ];

    return (
        <Box sx={{ padding: "2rem" }}>
            <Box sx={{ marginBottom: "40px" }}>
                <GridPageHeader title={t("settings.questionnaires", "Questionnaires")} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t(
                        "settings.questionnaires.subtitle",
                        "Manage QSS versions per campaign for your products, ingredients and companies.",
                    )}
                </Typography>
            </Box>

            <Box
                component="div"
                sx={{
                    borderRadius: "16px",
                    border: `1px solid ${theme.custom.grey[500]}`,
                    background: "#FFF",
                }}
            >
                <Stack direction="row" padding={theme.spacing(2)}>
                    <FoodsToggleButton buttons={filterButtons} initialSelected={{ id: entityFilter }} />
                </Stack>

                <PrimaryGrid
                    enableViews={false}
                    enableDynamicRowHeight
                    isLoading={isLoading}
                    gridKey={gridKey}
                    columns={mrt_columns}
                    data={questionnaires ?? []}
                    menuItems={menuItems}
                    filters={gridFilters}
                    sort={gridSorting}
                    pagination={{
                        totalItems: totalItems ?? 0,
                        ...gridPagination,
                    }}
                    columnConfig={gridConfiguration}
                />
            </Box>
        </Box>
    );
};
